import type { BranchKey } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { assertNotEmpty } from '@festina-life/core'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useClose } from '@festina-life/ui/structure'
import { useQueryClient } from '@tanstack/react-query'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import { fetchMetaModelKeyPrefix } from '../../../../uiModels/api/keys'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { useRebaseWorkspace } from '../../../workspacesPanel/workspaceDetails/manageWorkspace/useRebaseWorkspace'
import { PushUnavailabilityTooltip } from './PushUnavailabilityTooltip'

type Props = {
  branchKey: BranchKey
  isPushEnabled: boolean
  label: string
  variant?: 'primary' | 'secondary'
  isShowingIcon: boolean
  onPush: (args: {
    branchName: string
    baseBranchName: string
    server: BranchKey['server']
  }) => Promise<unknown>
  isPending: boolean
  unavailableReasons: string[]
}

export const PushToBaseBranchButton = ({
  branchKey,
  isPushEnabled,
  label,
  variant,
  isShowingIcon,
  onPush,
  isPending,
  unavailableReasons,
}: Props) => {
  const translate = useTranslate()
  const conciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: branchKey.name,
    shouldFallbackToStandard: false,
  })
  const rebaseWorkspace = useRebaseWorkspace()
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const singleWorkspace =
    branchWorkspaces?.length === 1 ? branchWorkspaces[0] : undefined

  const invalidateBranch = useInvalidateBranchAfterHistoryRewrite()
  const queryClient = useQueryClient()
  const close = useClose()

  const handlePushToBaseBranch = async () => {
    assertNotEmpty(conciseBranch?.baseBranchName)

    try {
      await onPush({
        branchName: branchKey.name,
        server: branchKey.server,
        baseBranchName: conciseBranch.baseBranchName,
      })

      void invalidateBranch({
        name: conciseBranch.baseBranchName,
        server: branchKey.server,
      })

      void queryClient.invalidateQueries({
        queryKey: fetchMetaModelKeyPrefix({
          server: branchKey.server,
          branch: branchKey.name,
        }),
      })

      if (singleWorkspace) {
        const workspaceKey = {
          name: singleWorkspace.name ?? '',
          branchKey,
        }
        await rebaseWorkspace(workspaceKey)
      }

      toast({
        title: translate('branchesTree.toast.pushedBranch', {
          branchName: branchKey.name,
          baseBranchName: conciseBranch?.baseBranchName,
        }),
        variant: 'success',
      })
      close()
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    }
  }

  return (
    <OperationsLoadedButton
      startIcon={isShowingIcon ? 'ChevronDoubleRight' : undefined}
      disabled={!isPushEnabled}
      onClick={handlePushToBaseBranch}
      isLoading={isPending}
      variant={variant}
      tip={
        unavailableReasons.length ? (
          <PushUnavailabilityTooltip unavailableReasons={unavailableReasons} />
        ) : undefined
      }
    >
      {label}
    </OperationsLoadedButton>
  )
}
